import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import type { Organizer } from '@/data/mockTableBookingData';
import { apiService } from '@/services/api';
import Pagination from '@/components/common/Pagination/Pagination';
import styles from './OrganizerListing.module.scss';

const ORG_PAGE_SIZE = 12;

interface OrganizerListingState {
  searchTerm: string;
  selectedCity: string;
  selectedRating: string;
  organizers: Organizer[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
}

function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    return <WrappedComponent {...props} navigate={navigate} />;
  };
}

class OrganizerListing extends Component<any, OrganizerListingState> {
  constructor(props: any) {
    super(props);
    this.state = {
      searchTerm: '',
      selectedCity: 'all',
      selectedRating: 'all',
      organizers: [],
      isLoading: true,
      error: null,
      currentPage: 1
    };
  }

  async componentDidMount() {
    try {
      await this.loadOrganizers();
    } catch (error) {
      console.error('[OrganizerListing] Error in componentDidMount:', error);
      this.setState({
        organizers: [],
        isLoading: false,
        error: 'Failed to load organizers. Please try again later.'
      });
    }
  }

  loadOrganizers = async () => {
    this.setState({ isLoading: true, error: null });
    
    try {
      // Add timeout to prevent hanging
      const apiCall = apiService.getActiveOrganizers();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      let response: any;
      try {
        response = await Promise.race([apiCall, timeoutPromise]) as any;
      } catch (timeoutError) {
        console.warn('[OrganizerListing] API call timed out or failed');
        throw timeoutError;
      }
      
      console.log('[OrganizerListing] Active organizers API response:', response);

      if (response) {
        const payload: any = response as any;
        const container: any = (payload && (payload.data ?? payload)) ?? {};
        let rawList: any = Array.isArray(container)
          ? container
          : (container.organizers || container.organizations || container.items || container.rows || container.list || container.results || container.data);

        if (!Array.isArray(rawList)) {
          const firstArray = Object.values(container).find((v: any) => Array.isArray(v));
          rawList = Array.isArray(firstArray) ? firstArray : [];
        }

        if (!Array.isArray(rawList)) {
          console.warn('[OrganizerListing] Unexpected organizers payload shape; received:', payload);
          this.setState({ 
            organizers: [], 
            isLoading: false 
          });
          return;
        }

        // Map API response to Organizer interface used in OrganizerListing
        // Prefer organizerProfile.displayName and organizerProfile.organizerLogoUrl from API; use these only (fallback to other fields only when organizerProfile is missing)
        const DEFAULT_PROFILE_PIC = 'https://res.cloudinary.com/dqiswsi6l/image/upload/v1772036390/chatup24_logo_pewdkc.png';
        const profile = (org: any) => org.organizerProfile ?? org.organizer_profile ?? {};

        const mapped: Organizer[] = rawList.map((org: any, idx: number) => {
          const p = profile(org);
          const name = p.displayName ?? p.display_name ?? org.organization_name ?? org.title ?? org.name ?? org.contact_person_name ?? `Organizer ${idx + 1}`;
          const city = org.city || org.location?.city || 'Unknown';
          const state = org.state || org.location?.state || '';
          const country = org.country || org.location?.country || '';
          const addressParts = [org.address, city, state, country].filter(Boolean);
          const address = addressParts.length > 0 ? addressParts.join(', ') : 'Address not available';

          const logoUrl = p.organizerLogoUrl ?? p.organizer_logo_url ?? org.profile_pic_url ?? org.profilePicUrl ?? org.profile_pic ?? org.profilePic ?? '';
          const logo = (logoUrl && String(logoUrl).trim() !== '') ? logoUrl : DEFAULT_PROFILE_PIC;
          const image = org.coverImageUrl ?? org.cover_image_url ?? org.logoUrl ?? org.logo_url ?? org.image ?? 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=300&fit=crop&auto=format';
          
          return {
            id: (org.user_id || org.organizer_id || org.organizer_profile_id || org.id)?.toString?.() || `org-${idx + 1}`,
            name,
            logo,
            // Only surface real, API-provided values. Missing fields stay empty (''/0)
            // so the UI can hide them rather than showing invented placeholder data.
            description: p.description ?? org.description ?? org.about ?? org.bio ?? '',
            address,
            city,
            phone: org.phone || org.contact || org.contact_number || '',
            email: org.email || '',
            rating: Number(org.rating) || 0,
            reviewCount: Number(org.review_count || org.reviewCount || org.total_reviews || 0),
            branchCount: Number(org.branch_count || org.branchCount || org.total_branches || 1),
            status: (org.status === 'active' || org.is_active || org.isActive) ? 'active' : 'inactive',
            image
          };
        });

        console.log('[OrganizerListing] Mapped organizers count:', mapped.length, mapped);
        this.setState({ 
          organizers: mapped ?? [], 
          isLoading: false,
          error: null
        });
      } else {
        this.setState({ 
          organizers: [], 
          isLoading: false,
          error: null
        });
      }
    } catch (error: any) {
      console.error('[OrganizerListing] Failed to load active organizers:', error);
      this.setState({ 
        organizers: [], 
        isLoading: false,
        error: error?.message?.includes('timeout') 
          ? 'Request timed out. Please try again.' 
          : 'Failed to load organizers. Please try again later.'
      });
    }
  };

  handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 });
  };

  handleCityFilter = (city: string) => {
    this.setState({ selectedCity: city, currentPage: 1 });
  };

  handleRatingFilter = (rating: string) => {
    this.setState({ selectedRating: rating, currentPage: 1 });
  };

  handleOrganizerClick = (organizerId: string) => {
    // Navigate directly to branches view
    this.props.navigate(`/customer/organizers/${organizerId}?view=branches`);
  };

  getFilteredOrganizers = (): Organizer[] => {
    let filtered = [...this.state.organizers];

    // Filter by search term
    if (this.state.searchTerm) {
      const searchLower = this.state.searchTerm.toLowerCase();
      filtered = filtered.filter(
        org =>
          org.name.toLowerCase().includes(searchLower) ||
          org.address.toLowerCase().includes(searchLower) ||
          org.city.toLowerCase().includes(searchLower) ||
          org.description.toLowerCase().includes(searchLower)
      );
    }

    // Filter by city
    if (this.state.selectedCity !== 'all') {
      filtered = filtered.filter(org => org.city === this.state.selectedCity);
    }

    // Filter by rating
    if (this.state.selectedRating !== 'all') {
      const minRating = parseFloat(this.state.selectedRating);
      filtered = filtered.filter(org => org.rating >= minRating);
    }

    return filtered;
  };

  getUniqueCities = (): string[] => {
    const cities = this.state.organizers.map(org => org.city);
    return Array.from(new Set(cities)).sort();
  };

  renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className={styles.star}>★</span>);
    }

    if (hasHalfStar) {
      stars.push(<span key="half" className={styles.starHalf}>★</span>);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className={styles.starEmpty}>★</span>);
    }

    return stars;
  };

  render() {
    try {
      const { searchTerm, selectedCity, selectedRating, isLoading, error, currentPage } = this.state;
      const filteredOrganizers = this.getFilteredOrganizers();
      const uniqueCities = this.getUniqueCities();
      const orgStart = (currentPage - 1) * ORG_PAGE_SIZE;
      const pagedOrganizers = filteredOrganizers.slice(orgStart, orgStart + ORG_PAGE_SIZE);

      return (
      <>
        <DashboardHeader />
        <div className={styles.organizerListingContainer}>
          <div className={styles.organizerListingContent}>
            {/* Header Section */}
            <div className={styles.headerSection}>
              <h1 className={styles.pageTitle}>Find Organizers</h1>
              <p className={styles.pageSubtitle}>
                Discover venues and book your table, room or tournament
              </p>
              <button
                className={styles.browseCategoriesButton}
                onClick={() => this.props.navigate('/customer/categories')}
              >
                🗂️ Browse by category
              </button>
            </div>

            {/* Search and Filters */}
            <div className={styles.searchFiltersSection}>
              {/* Search Bar */}
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder="Search by name, location, or description..."
                  value={searchTerm}
                  onChange={this.handleSearchChange}
                  className={styles.searchInput}
                />
                <span className={styles.searchIcon}>🔍</span>
              </div>

              {/* Filters */}
              <div className={styles.filtersRow}>
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>City</label>
                  <select
                    value={selectedCity}
                    onChange={(e) => this.handleCityFilter(e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="all">All Cities</option>
                    {uniqueCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Minimum Rating</label>
                  <select
                    value={selectedRating}
                    onChange={(e) => this.handleRatingFilter(e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="all">All Ratings</option>
                    <option value="4.5">4.5+ Stars</option>
                    <option value="4.0">4.0+ Stars</option>
                    <option value="3.5">3.5+ Stars</option>
                    <option value="3.0">3.0+ Stars</option>
                  </select>
                </div>

                {(selectedCity !== 'all' || selectedRating !== 'all' || searchTerm) && (
                  <button
                    onClick={() => {
                      this.setState({
                        searchTerm: '',
                        selectedCity: 'all',
                        selectedRating: 'all',
                        currentPage: 1
                      });
                    }}
                    className={styles.clearFiltersButton}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className={styles.errorMessage}>
                <span>⚠️ {error}</span>
                <button
                  type="button"
                  onClick={() => this.loadOrganizers()}
                  className={styles.emptyStateButton}
                >
                  Try again
                </button>
              </div>
            )}

            {/* Results Count */}
            <div className={styles.resultsCount}>
              {isLoading ? (
                <span>Loading...</span>
              ) : (
                <span>
                  {filteredOrganizers.length} {filteredOrganizers.length === 1 ? 'organizer' : 'organizers'} found
                </span>
              )}
            </div>

            {/* Organizers Grid */}
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p>Loading organizers...</p>
              </div>
            ) : filteredOrganizers.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>🔍</div>
                <h3>No organizers found</h3>
                <p>Try adjusting your search or filters</p>
                <button
                  onClick={() => {
                    this.setState({
                      searchTerm: '',
                      selectedCity: 'all',
                      selectedRating: 'all'
                    });
                  }}
                  className={styles.emptyStateButton}
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <>
                <div className={styles.organizersGrid}>
                  {pagedOrganizers.map(organizer => (
                    <div
                      key={organizer.id}
                      className={styles.organizerCard}
                      onClick={() => this.handleOrganizerClick(organizer.id)}
                    >
                      <div className={styles.organizerImage}>
                        <img
                          src={organizer.logo || organizer.image || 'https://res.cloudinary.com/dqiswsi6l/image/upload/v1772036390/chatup24_logo_pewdkc.png'}
                          alt={organizer.name}
                        />
                        {organizer.rating > 0 && (
                          <div className={styles.ratingPill}>★ {organizer.rating.toFixed(1)}</div>
                        )}
                      </div>

                      <div className={styles.organizerInfo}>
                        <h3 className={styles.organizerName}>{organizer.name}</h3>
                        <div className={styles.organizerMeta}>
                          <span className={styles.metaLoc}>📍 {organizer.city}</span>
                          <span className={styles.metaDot}>·</span>
                          <span>{organizer.branchCount} {organizer.branchCount === 1 ? 'branch' : 'branches'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Pagination
                  page={currentPage}
                  total={filteredOrganizers.length}
                  pageSize={ORG_PAGE_SIZE}
                  onChange={(p) => { this.setState({ currentPage: p }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                />
              </>
            )}
          </div>
        </div>
        <Footer />
      </>
      );
    } catch (renderError) {
      console.error('[OrganizerListing] Error in render:', renderError);
      // Fallback render if something goes wrong
      return (
        <>
          <DashboardHeader />
          <div className={styles.organizerListingContainer}>
            <div className={styles.organizerListingContent}>
              <div className={styles.errorState}>
                <h2>Something went wrong</h2>
                <p>Please refresh the page or try again later.</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className={styles.emptyStateButton}
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
          <Footer />
        </>
      );
    }
  }
}

export default withNavigation(OrganizerListing);

